import { createSelector } from '@reduxjs/toolkit';
import { selectors as dataGridTableSelectors } from '../Modules/dataGridTable';
import { selectors as filtersSelectors } from '../Modules/filters';
import { getGlobalDepartments } from 'utils/personnelHelper';
import {
  getDepartmentNamesArray,
  getItemOrDefaultToValue,
  nestedDisplay,
} from 'utils/details';
import { DEFAULT_EMPTY_VALUE, ROLES } from 'utils/constants';
import {
  convertActivityTypeToUserStatus,
  reorderAccordingRecentActivity,
  translateActivityType,
} from '../Layouts/attendanceTrackingHelper';
import { displayTime, formatDuration } from 'utils/time';
import { getTimezone } from 'reducers/utils';
import { getGlobalDepartmentRecords } from 'selectors/globalSelectors';
import { isEmpty } from 'lodash';
import { checkForSpecificRole } from 'utils/DataTransform/Personnel/PersonnelDetails';
import {
  isDeletingActivityTypeAllowed,
  isEditingActivityTypeAllowed,
  shiftColumn,
} from '../Layouts/option';

export const getTotalEntries = createSelector(
  dataGridTableSelectors.getTotalGridRows,
  rowsCount => {
    if (!rowsCount) {
      return 0;
    }
    return rowsCount;
  },
);
// status of list refresh
export const getForceRefresh = createSelector(
  dataGridTableSelectors.getForceRefresh,
  forceRefresh => forceRefresh,
);

export const getSelectedRowID = createSelector(
  dataGridTableSelectors.getSelectedRowID,
  selectedRowID => selectedRowID,
);
export const getFilters = createSelector(
  filtersSelectors.getFilterValues,
  filterValues => {
    return filterValues;
  },
);

export const getAttendanceListTransformed = createSelector(
  dataGridTableSelectors.getGridTableList,
  getTimezone,
  getGlobalDepartmentRecords,
  (list, timezone, globalDepartmentRecords) => {
    if (!list) return [];
    const globalDepartmentList = getGlobalDepartments(globalDepartmentRecords);

    return list.map(shift => ({
      /**
       * TODO: make this a transform function?
       */

      id: shift.id,
      employeeId: shift.user
        ? getItemOrDefaultToValue(
            shift.user.general,
            'employeeId',
            DEFAULT_EMPTY_VALUE,
          )
        : DEFAULT_EMPTY_VALUE,
      employeeName: shift.user?.general?.firstName
        ? `${shift.user.general.firstName} ${shift.user.general.lastName}`
        : DEFAULT_EMPTY_VALUE,
      shiftStatus: shift.lastActivityType
        ? convertActivityTypeToUserStatus(shift.lastActivityType)
        : DEFAULT_EMPTY_VALUE,
      startShift: shift.startTime
        ? displayTime({ timestamp: shift.startTime, timezone })
        : DEFAULT_EMPTY_VALUE,
      endShift: shift.stopTime
        ? displayTime({ timestamp: shift.stopTime, timezone })
        : DEFAULT_EMPTY_VALUE,
      reporterId: shift.reporterId ? shift.reporterId : DEFAULT_EMPTY_VALUE,
      reporterName: shift.createdBy?.general?.firstName
        ? `${shift.createdBy.general.firstName} ${shift.createdBy.general.lastName}`
        : DEFAULT_EMPTY_VALUE,
      duration: shift.duration
        ? formatDuration(shift.duration * 60) //duration is stored in minutes but format expects seconds
        : DEFAULT_EMPTY_VALUE,
      departments:
        shift.user.general?.departmentIds && globalDepartmentList.length > 0
          ? getDepartmentNamesArray(globalDepartmentList, shift.user.general)
          : DEFAULT_EMPTY_VALUE,
    }));
  },
);

const globalUserInfo = state => state.globalList.users.record;
export const getAttendanceDetailsTransformed = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  globalUserInfo,
  state => state.globalList.departments,
  (rawAttendanceDetails, globalUserRecord, departmentObject) => {
    if (isEmpty(rawAttendanceDetails)) {
      return {};
    }

    const reorderedActivities = reorderAccordingRecentActivity(
      rawAttendanceDetails.activities,
    );

    const globalDepartmentList = getGlobalDepartments(departmentObject?.record);

    const selectedDepartments = globalDepartmentList.filter(f =>
      getItemOrDefaultToValue(
        rawAttendanceDetails.user.general,
        'departmentIds',
        [],
      ).includes(f.id),
    );
    const roles = getItemOrDefaultToValue(
      rawAttendanceDetails.user.general,
      'roles',
      [],
    );

    return {
      generalInfo: {
        employeeId: {
          type: 'text',
          label: 'Employee ID',
          value: getItemOrDefaultToValue(
            rawAttendanceDetails.user.general,
            'employeeId',
            '-',
          ),
        },
        firstName: {
          type: 'text',
          value: getItemOrDefaultToValue(
            rawAttendanceDetails.user.general,
            'firstName',
            '-',
          ),
        },
        lastName: {
          type: 'text',
          value: getItemOrDefaultToValue(
            rawAttendanceDetails.user.general,
            'lastName',
            '-',
          ),
        },
        //show phone when role has responder/supervisor/manager/driver
        ...(checkForSpecificRole(roles, [
          ROLES.RESPONDER.id,
          ROLES.SUPERVISOR.id,
          ROLES.MANAGER.id,
          ROLES.DRIVER.id,
        ]) && {
          phone: {
            type: 'text',
            value:
              rawAttendanceDetails.user.general.phoneNumber !== null
                ? `${getItemOrDefaultToValue(
                    rawAttendanceDetails.user.general.phoneNumber,
                    'prefix',
                  )} ${getItemOrDefaultToValue(
                    rawAttendanceDetails.user.general.phoneNumber,
                    'number',
                    '-',
                  )}`
                : '-',
          },
        }),
        //show email when role has responder/supervisor/manager/driver
        ...(checkForSpecificRole(roles, [
          ROLES.RESPONDER.id,
          ROLES.SUPERVISOR.id,
          ROLES.MANAGER.id,
          ROLES.DRIVER.id,
        ]) && {
          email: {
            type: 'text',
            value: getItemOrDefaultToValue(
              rawAttendanceDetails.user.general,
              'email',
              '-',
            ),
          },
        }),
        departments: {
          type: 'array',
          value: nestedDisplay(selectedDepartments),
        },
        shiftStatus: {
          type: 'icons',
          label: 'Shift Status',
          value: convertActivityTypeToUserStatus(
            rawAttendanceDetails.lastActivityType,
          ),
        },
      },
      detailedInfo: {
        ...(rawAttendanceDetails.activities && {
          shiftBreakdown: {
            type: 'list-data',
            label: 'Shift Breakdown',
            value: {
              data: reorderedActivities.map(item => ({
                activityType:
                  translateActivityType(item.activityType) ||
                  DEFAULT_EMPTY_VALUE,
                activityTime: item.activityTime
                  ? displayTime({ timestamp: item.activityTime })
                  : DEFAULT_EMPTY_VALUE,
                reporter: item.createdById
                  ? globalUserRecord[item.createdById]
                  : DEFAULT_EMPTY_VALUE,
                duration: item.duration
                  ? formatDuration(item.duration * 60) //the duration is in minutes the formatter expects seconds
                  : DEFAULT_EMPTY_VALUE,
                action: {
                  type: 'button',
                  allowableActions: {
                    allowEdit: isEditingActivityTypeAllowed(item.activityType),
                    allowDelete: isDeletingActivityTypeAllowed(
                      item.activityType,
                    ),
                  },
                  itemData: {
                    id: item.id,
                    shiftInfo: item,
                    attendanceTrackingObjectId: rawAttendanceDetails.id,
                  },
                },
              })),
              columns: shiftColumn,
            },
            view: 'managerial',
          },
        }),
      },
    };
  },
);
export const getAttendanceDetailsRaw = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (!details) return {};
    return details;
  },
);
